import { SettingOutlined, SyncOutlined, FileSyncOutlined } from '@ant-design/icons';
import { Button, Card, Col, Popconfirm, Row, Skeleton, Tag, Tooltip, message } from 'antd';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from '../../components/loading/Spinner';
import { ErrorText } from '../../components/text/ErrorText';
import { getAlgorithm, getOverview, getShowableAlgorithmVersions } from '../../lib/api/algorithm-api';
import {
  calculateScoringForAlgorithm,
  getAlgorithmOverviewDescription,
  getAlgorithmValue,
} from '../../lib/util/algorithm';
import { Algorithm, AlgorithmVersion } from '../../types';
import { ClientScoring, OverviewLine, OverviewSummary } from '../../types/result';
import { DatasetScoreLineNew } from './DatasetScoreLineNew';
import { DatasetSettingsModal } from './DatasetSettingsModal';
import { CONFIG } from '../../config/Config';

type Props = {
  clientAlgorithm: ClientScoring;
  scoringIdLookup: { algorithmDataset: string; id: string }[];
};

export const AlgorithmCard = ({ clientAlgorithm, scoringIdLookup }: Props) => {
  const [algorithm, setAlgorithm] = useState<Algorithm>();
  const [latestAlgorithmVersion, setLatestAlgorithmVersion] = useState<AlgorithmVersion>();
  const [previousAlgorithmVersion, setPreviousAlgorithmVersion] = useState<AlgorithmVersion>();
  const [loading, setLoading] = useState<boolean>(false);
  const [recalculating, setRecalculating] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [datasetSettingsVisible, setDatasetSettingsVisible] = useState<boolean>(false);
  const [mergedScores, setMergedScores] = useState<OverviewLine[] | null>(null);

  const getVersions = useCallback(async () => {
    if (algorithm) return;
    try {
      setLoading(true);
      const algorithm = await getAlgorithm(clientAlgorithm.algorithm_id.toString());

      const algoVersions = await getShowableAlgorithmVersions(clientAlgorithm.algorithm_id.toString());
      const showableVersions = algoVersions.sort((a, b) => (a.timeCreated < b.timeCreated ? 1 : -1));

      setAlgorithm(algorithm);
      setLatestAlgorithmVersion(showableVersions[0]);
      if (showableVersions.length > 1) {
        setPreviousAlgorithmVersion(showableVersions[1]);
      }

      let latest: { id: string; dataset_version: string; value: OverviewSummary }[] = [];
      let previous: { id: string; dataset_version: string; value: OverviewSummary }[] = [];
      if (showableVersions[0]) {
        const result = await getOverview(showableVersions[0].id);
        latest = Object.keys(result).map(key => {
          return { id: result[key].dataset_id, dataset_version: key, value: result[key] };
        });
      }
      if (showableVersions[1]) {
        const result = await getOverview(showableVersions[1].id);
        previous = Object.keys(result).map(key => {
          return { id: result[key].dataset_id, dataset_version: key, value: result[key] };
        });
      }
      const mergedValues = clientAlgorithm.datasets.map((datasetKey: any) => {
        const latestDataset = _.find(latest, { id: datasetKey });
        const previousDataset = _.find(previous, { id: datasetKey });

        const datasetName = latestDataset
          ? latestDataset.value.dataset_name
          : previousDataset
          ? previousDataset.value.dataset_name
          : null;

        return {
          datasetKey: datasetKey,
          datasetName: datasetName,
          datasetVersion: latestDataset?.dataset_version!,
          latest: latestDataset,
          previous: previousDataset,
        };
      });

      setMergedScores(mergedValues);
      setError(false);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [algorithm, clientAlgorithm.algorithm_id, clientAlgorithm.datasets]);

  useEffect(() => {
    getVersions();
  }, [getVersions]);

  const recomputeScoring = async (preproduction: boolean) => {
    if (latestAlgorithmVersion) {
      try {
        setRecalculating(true);
        await calculateScoringForAlgorithm(clientAlgorithm.algorithm_id, latestAlgorithmVersion.version, preproduction);
        message.success('Recompute scoring done.');
      } catch (error) {
        message.error('Error recomputing scoring.');
      } finally {
        setRecalculating(false);
      }
    }
  };

  return (
    <>
      <Col key={clientAlgorithm.algorithm_id} xl={{ span: 12 }} sm={{ span: 24 }}>
        <Card
          title={
            <Row className="items-center" style={{ whiteSpace: 'normal' }}>
              {loading ? (
                <Spinner />
              ) : (
                <h4 className="text-toolbox-secondary text-xl font-bold m-0 mr-5">
                  {getAlgorithmValue(algorithm?.name || '').toUpperCase()}
                </h4>
              )}
              {!loading && !error && (
                <div className="flex justify-between flex-grow items-center">
                  <div>
                    <Tag color={'#005ea0'}>{latestAlgorithmVersion?.version}</Tag>
                    <Tag color={'#444444'}>{previousAlgorithmVersion?.version}</Tag>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => setDatasetSettingsVisible(true)} type="link" className="p-0" size="large">
                      <Tooltip title="Add/remove datasets">
                        <SettingOutlined className="text-gray-400" />
                      </Tooltip>
                    </Button>
                    <Popconfirm
                      title="Are you sure to recompute scoring for this algorithm?"
                      onConfirm={() => recomputeScoring(false)}
                      okText="Yes"
                      cancelText="No"
                      placement="topRight">
                      <Button type="link" className="p-0 recompute-button" size="large" loading={recalculating}>
                        <Tooltip title="Recompute scoring" placement="bottom">
                          <SyncOutlined className="text-gray-400" />
                        </Tooltip>
                      </Button>
                    </Popconfirm>
                    {CONFIG.backendUrl.includes('datasets') && (
                      <Popconfirm
                        title="Are you sure to recompute scoring on pre-production?"
                        onConfirm={() => recomputeScoring(true)}
                        okText="Yes"
                        cancelText="No"
                        placement="topRight">
                        <Button type="link" className="p-0 recompute-button" size="large" loading={recalculating}>
                          <Tooltip title="Recompute scoring with datasets" placement="bottom">
                            <FileSyncOutlined className="text-gray-400" />
                          </Tooltip>
                        </Button>
                      </Popconfirm>
                    )}
                  </div>
                </div>
              )}
            </Row>
          }
          style={{ minHeight: 300, marginBottom: 20 }}
          bodyStyle={{ minHeight: 300 }}>
          {!loading && error && <ErrorText>Something went wrong. Algorithm could not be found.</ErrorText>}
          {!error && (
            <div
              style={{
                minHeight: 300,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}>
              <div>
                <div style={{ marginTop: '10px', fontSize: 16, paddingRight: 50, width: '100%' }}>
                  {loading ? (
                    <Skeleton title={false} paragraph={{ rows: 2, width: '100%' }} />
                  ) : (
                    getAlgorithmOverviewDescription(algorithm?.name || '')
                  )}
                </div>
                <Row className="container" style={{ marginTop: 20 }}>
                  {loading ? (
                    <Skeleton title={false} paragraph={{ rows: 2, width: '100%' }} />
                  ) : (
                    algorithm &&
                    mergedScores && (
                      <>
                        <Row className="container">
                          <Col span={10}>
                            <p className="section-title" style={{ color: '#7d7e82', margin: 7 }}>
                              Dataset
                            </p>
                          </Col>
                          <Col span={6}>
                            <p className="section-title" style={{ color: '#7d7e82', margin: 7 }}>
                              Field Score
                            </p>
                          </Col>
                          <Col span={6}>
                            <p className="section-title" style={{ color: '#7d7e82', margin: 7 }}>
                              Completely Correct
                            </p>
                          </Col>
                        </Row>
                        {mergedScores
                          .filter(mergedValue => !!mergedValue.datasetKey)
                          .map(mergedValue => {
                            return (
                              <DatasetScoreLineNew
                                key={mergedValue['datasetKey' as keyof {}]}
                                datasetId={mergedValue['datasetKey' as keyof {}]}
                                algorithm={algorithm}
                                latestAlgorithmVersion={latestAlgorithmVersion}
                                previousAlgorithmVersion={previousAlgorithmVersion}
                                overviewLine={mergedValue}
                              />
                            );
                          })}
                      </>
                    )
                  )}
                </Row>
              </div>
              <Row justify="end" style={{ margin: '10px 0 20px 0' }}>
                <Link
                  className="white ant-btn ant-btn-primary"
                  style={{ marginTop: '10px' }}
                  to={`/overview/${algorithm?.id}`}>
                  Version Overview
                </Link>
                <Link
                  className="white ant-btn ant-btn-primary"
                  style={{ marginTop: '10px', marginLeft: '20px' }}
                  to={`/demo/${algorithm?.id}/models/${latestAlgorithmVersion?.version}`}>
                  Test Model
                </Link>
              </Row>
            </div>
          )}
        </Card>
      </Col>
      <DatasetSettingsModal
        visible={datasetSettingsVisible}
        onClose={() => setDatasetSettingsVisible(false)}
        clientAlgorithm={clientAlgorithm}
        scoringIdLookup={scoringIdLookup}
      />
    </>
  );
};
